import axios from 'axios'
import { PublicClientApplication } from '@azure/msal-browser'
import { apiRequest } from './auth'
import type { PriorityAction } from './posture-brief'
import type {
  OAuthAppSummary,
  OAuthAppsPagedResponse,
  OAuthAppDetail,
  AcknowledgeOAuthAppRequest,
  ConsentRecordRow,
  PermissionHeatmap,
} from '@/components/SecurityCenter/appPermissionsTypes'
import type {
  ExecutiveReportData,
  ScoreWithTrend,
  FrameworkScoreSummary,
  OperationalMetrics,
  ScoreDataPoint,
} from '@/types/executiveReport'
import type {
  ConfigurationDrift,
  DashboardStats,
  DashboardTrends,
  MetricTrendResult,
  Tenant,
  ApiResponse,
  ScubaReport,
  ScubaSummary,
  ScubaSecurityRisk,
  ScubaScanResponse,
  UserInsightsSummary,
  LicenseSkuSummary,
  UserDetailListResult,
  UserIssueFilter,
  RefreshStatusInfo,
  RefreshResponse,
  PhishResistantMfaSummary,
  UserInsightsTrendsResult,
  SecureScoreSummary,
  SecureScoreResult,
  SecureScoreControlProfile,
  // Standards Compliance types
  ComplianceStandard,
  CompliancePolicy,
  TenantStandardAssignment,
  ComplianceEvaluation,
  ComplianceViolation,
  TenantPolicyWaiver,
  ComplianceSummary,
  ComplianceTrendPoint,
  ScoreEvent,
  ControlChange,
  AssignStandardRequest,
  UpdateTenantStandardRequest,
  CreateWaiverRequest,
  UpdateWaiverRequest,
  ViolationFilters,
  OpaHealthStatus,
  EvaluationResult,
  PaginatedResponse,
  ViolationSummaryResponse,
  // QA Policy Validation types
  QaValidationSummary,
  QaPolicy,
  UpsertQaValidationRequest,
} from '@/types'

import { isDemoMode } from './demo/flag'
import { demoAdapter } from './demo/demoAdapter'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://iuvo-guardian-prod-kdsyjl-func-win.azurewebsites.net/api'

// Create axios instance. In demo mode the custom adapter resolves every
// request from local mock data — no auth, no network — so the SPA can
// be served as a static iframe on iuvotech.com without any backend.
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  ...(isDemoMode ? { adapter: demoAdapter } : {}),
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config) => {
    // Get MSAL instance from window (set in main.tsx)
    const msalInstance = (window as any).msalInstance as PublicClientApplication
    
    if (msalInstance) {
      try {
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          const tokenRequest = {
            ...apiRequest,
            account: accounts[0],
          }
          
          try {
            // Try silent acquisition only - no fallback to interactive
            const response = await msalInstance.acquireTokenSilent(tokenRequest)
            config.headers.Authorization = `Bearer ${response.accessToken}`
          } catch (silentError) {
            console.log('Silent token acquisition failed:', silentError)
            // Don't throw here - just continue without token
            // The backend will handle authentication if needed
          }
        } else {
          console.log('No accounts available')
          // Continue without token - Azure Function has its own auth
        }
      } catch (error) {
        console.warn('Failed to acquire access token:', error)
        // Don't add token if acquisition fails
      }
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token is invalid/expired - redirect to login
      const msalInstance = (window as any).msalInstance as PublicClientApplication

      if (msalInstance) {
        console.log('401 Unauthorized - redirecting to login')
        // Clear any cached tokens
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          await msalInstance.logout({ account: accounts[0] })
        } else {
          // Force login redirect
          msalInstance.loginRedirect(apiRequest)
        }
      }
    }
    return Promise.reject(error)
  }
)

// ── Executive Report normalization helpers ──
// The Container API may return PascalCase or camelCase keys depending on
// serializer settings; these helpers accept either form defensively.

function normalizeScoreWithTrend(s: any): ScoreWithTrend {
  if (!s) return { label: '', current: null, periodStart: null, change: null, direction: 'flat', weight: 0 }
  return {
    label: s.Label ?? s.label ?? '',
    current: s.Current ?? s.current ?? null,
    periodStart: s.PeriodStart ?? s.periodStart ?? null,
    change: s.Change ?? s.change ?? null,
    direction: s.Direction ?? s.direction ?? 'flat',
    weight: s.Weight ?? s.weight ?? 0,
  }
}

function normalizeFramework(f: any): FrameworkScoreSummary {
  return {
    code: f.Code ?? f.code ?? '',
    name: f.Name ?? f.name ?? '',
    score: f.Score ?? f.score ?? 0,
    passedControls: f.PassedControls ?? f.passedControls ?? 0,
    totalControls: f.TotalControls ?? f.totalControls ?? 0,
    periodStartScore: f.PeriodStartScore ?? f.periodStartScore ?? null,
    change: f.Change ?? f.change ?? null,
    direction: f.Direction ?? f.direction ?? 'flat',
    includeInGuardianScore: f.IncludeInGuardianScore ?? f.includeInGuardianScore ?? false,
  }
}

function normalizeMetrics(m: any): OperationalMetrics {
  if (!m) return { violationsResolved: 0, driftItemsRemediated: 0, waiversGranted: 0, openCriticalViolations: 0, openHighViolations: 0, totalDriftDetected: 0 }
  return {
    violationsResolved: m.ViolationsResolved ?? m.violationsResolved ?? 0,
    driftItemsRemediated: m.DriftItemsRemediated ?? m.driftItemsRemediated ?? 0,
    waiversGranted: m.WaiversGranted ?? m.waiversGranted ?? 0,
    openCriticalViolations: m.OpenCriticalViolations ?? m.openCriticalViolations ?? 0,
    openHighViolations: m.OpenHighViolations ?? m.openHighViolations ?? 0,
    totalDriftDetected: m.TotalDriftDetected ?? m.totalDriftDetected ?? 0,
  }
}

function normalizeDataPoint(p: any): ScoreDataPoint {
  return {
    date: p.Date ?? p.date ?? '',
    guardianScore: p.GuardianScore ?? p.guardianScore ?? null,
    secureScore: p.SecureScore ?? p.secureScore ?? null,
    bpaScore: p.BpaScore ?? p.bpaScore ?? null,
    mfaAdoption: p.MfaAdoption ?? p.mfaAdoption ?? null,
    baselineCompliance: p.BaselineCompliance ?? p.baselineCompliance ?? null,
  }
}

export const api = {
  // Dashboard APIs
  async getDashboardStats(tenantId: string): Promise<DashboardStats> {
    try {
      const response = await apiClient.get(`/dashboard/tenant/${tenantId}`)
      const complianceDashboard = response.data

      if (import.meta.env.DEV) {
        console.log('API Response:', complianceDashboard)
      }
      
      // Handle C# PascalCase property names
      const overallScore = complianceDashboard.OverallScore || complianceDashboard.overallScore
      const metrics = complianceDashboard.Metrics || complianceDashboard.metrics
      const lastUpdated = complianceDashboard.LastUpdated || complianceDashboard.lastUpdated
      
      // Transform C# ComplianceDashboard to React DashboardStats
      return {
        totalTenants: 1, // Single tenant for now
        activeDrifts: metrics?.ActiveAlerts || metrics?.activeAlerts || 0,
        pendingProcessing: metrics?.PendingProcessing || metrics?.pendingProcessing || 0,
        criticalIssues: overallScore?.IssuesBySeverity?.Critical || overallScore?.issuesBySeverity?.Critical || 0,
        issuesBySeverity: {
          critical: overallScore?.IssuesBySeverity?.Critical || overallScore?.issuesBySeverity?.Critical || 0,
          high: overallScore?.IssuesBySeverity?.High || overallScore?.issuesBySeverity?.High || 0,
          medium: overallScore?.IssuesBySeverity?.Medium || overallScore?.issuesBySeverity?.Medium || 0,
          low: overallScore?.IssuesBySeverity?.Low || overallScore?.issuesBySeverity?.Low || 0,
        },
        complianceScore: (() => {
          const totalChecks = overallScore?.TotalChecks || overallScore?.totalChecks || 0
          const passedChecks = overallScore?.PassedChecks || overallScore?.passedChecks || 0
          const failedChecks = overallScore?.FailedChecks || overallScore?.failedChecks || 0
          
          // Calculate correct percentage: passed / total * 100
          // Use Math.floor to be conservative - any failed checks should prevent 100%
          const calculatedPercentage = totalChecks > 0 ? Math.floor((passedChecks / totalChecks) * 100) : 0
          
          // Determine level based on calculated percentage
          let level: string
          if (calculatedPercentage >= 95) level = 'Excellent'
          else if (calculatedPercentage >= 85) level = 'Good'
          else if (calculatedPercentage >= 75) level = 'Fair'
          else if (calculatedPercentage >= 60) level = 'Poor'
          else level = 'Critical'
          
          return {
            percentage: calculatedPercentage,
            level,
            totalChecks,
            passedChecks,
            failedChecks,
            lastUpdated: lastUpdated || new Date().toISOString()
          }
        })(),
        recentActivity: await this.getRecentActivity(tenantId),
        driftTrends: await this.getDriftTrends(tenantId),  
        topRisks: [] // TODO: Map from critical drift
      } as DashboardStats
    } catch (error) {
      console.error('Dashboard API Error:', error)
      // Return fallback data
      return {
        totalTenants: 1,
        activeDrifts: 0,
        pendingProcessing: 0,
        criticalIssues: 0,
        issuesBySeverity: { critical: 0, high: 0, medium: 0, low: 0 },
        complianceScore: {
          percentage: 0,
          level: 'Critical',
          totalChecks: 0,
          passedChecks: 0,
          failedChecks: 0,
          lastUpdated: new Date().toISOString()
        },
        recentActivity: [],
        driftTrends: [],
        topRisks: []
      } as DashboardStats
    }
  },

  // Dashboard Trends API (week-over-week indicators)
  async getDashboardTrends(tenantId: string): Promise<DashboardTrends | null> {
    try {
      const response = await apiClient.get(`/metrictrends/tenant/${tenantId}`)
      const data = response.data

      // Handle C# PascalCase property names
      return {
        secureScore: this.normalizeMetricTrendResult(data.SecureScore || data.secureScore),
        mfaAdoption: this.normalizeMetricTrendResult(data.MfaAdoption || data.mfaAdoption),
        baselineCompliance: this.normalizeMetricTrendResult(data.BaselineCompliance || data.baselineCompliance),
        generatedAt: data.GeneratedAt || data.generatedAt || new Date().toISOString()
      }
    } catch (error: any) {
      // 404 is expected when no snapshots exist yet
      if (error.response?.status === 404) {
        console.log('No trend data available yet for tenant', tenantId)
        return null
      }
      console.error('Dashboard trends API error:', error)
      return null
    }
  },

  // Helper to normalize metric trend result from C# PascalCase
  normalizeMetricTrendResult(data: any): MetricTrendResult {
    if (!data) {
      return {
        metricType: '',
        currentValue: 0,
        previousWeekValue: null,
        changeAmount: null,
        changePercentage: null,
        direction: 'flat',
        history: []
      }
    }
    return {
      metricType: data.MetricType || data.metricType || '',
      currentValue: data.CurrentValue || data.currentValue || 0,
      previousWeekValue: data.PreviousWeekValue ?? data.previousWeekValue ?? null,
      changeAmount: data.ChangeAmount ?? data.changeAmount ?? null,
      changePercentage: data.ChangePercentage ?? data.changePercentage ?? null,
      direction: (data.Direction || data.direction || 'flat') as 'up' | 'down' | 'flat',
      history: (data.History || data.history || []).map((h: any) => ({
        date: h.Date || h.date,
        value: h.Value || h.value,
        context: h.Context || h.context
      }))
    }
  },

  // Baseline APIs
  async getBaseline(tenantId: string): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>(`/dashboard/tenant/${tenantId}/baseline`)
    return response.data.data
  },

  async hasBaseline(tenantId: string): Promise<boolean> {
    const response = await apiClient.get<ApiResponse<boolean>>(`/dashboard/tenant/${tenantId}/baseline/exists`)
    return response.data.data
  },

  async getComplianceFrameworks(): Promise<any[]> {
    const response = await apiClient.get<ApiResponse<any[]>>('/dashboard/frameworks')
    return response.data.data
  },

  // Configuration Drift APIs
  async getConfigurationDrifts(params?: {
    tenantId?: string
    severity?: string
    status?: string
    fromDate?: string
    toDate?: string
    page?: number
    limit?: number
    unprocessedOnly?: boolean // When true, only returns drifts with ProcessedAt == null (not yet processed to ADO)
  }): Promise<{ items: ConfigurationDrift[], total: number, page: number, limit: number }> {
    const response = await apiClient.get<ApiResponse<{
      items: ConfigurationDrift[]
      total: number
      page: number
      limit: number
    }>>('/configurationdrift', { params })
    return response.data.data
  },

  async getConfigurationDrift(id: string): Promise<ConfigurationDrift> {
    const response = await apiClient.get<ApiResponse<ConfigurationDrift>>(`/configurationdrift/${id}`)
    return response.data.data
  },

  async updateDriftStatus(id: string, status: string, comment?: string): Promise<ConfigurationDrift> {
    const response = await apiClient.patch<ApiResponse<ConfigurationDrift>>(
      `/configurationdrift/${id}/status`,
      { status, comment }
    )
    return response.data.data
  },

  async approveDrift(id: string, comment?: string): Promise<{
    data: ConfigurationDrift
    autoApprovedCount: number
    message: string
  }> {
    // Approve returns a flat envelope (data + autoApprovedCount + message),
    // not the standard ApiResponse<T> wrapper, because the toast surfaces
    // both the message and the auto-approved count from a single call.
    const response = await apiClient.post<{
      data: ConfigurationDrift
      success: boolean
      message: string
      autoApprovedCount: number
    }>(`/configurationdrift/${id}/approve`, { comment })
    return response.data
  },

  async rejectDrift(id: string, comment?: string): Promise<ConfigurationDrift> {
    const response = await apiClient.post<ApiResponse<ConfigurationDrift>>(
      `/configurationdrift/${id}/reject`,
      { comment }
    )
    return response.data.data
  },

  // Priority Actions API — server-side ranked posture queue. Endpoint
  // returns the array directly (no ApiResponse<T> envelope), matching the
  // PriorityActionsController.cs Ok(actions) shape. See:
  // .agents/Tasks/High Priority/operations-priority-alerts-curation.md §7.2
  async getPriorityActions(
    tenantId: string,
    options?: {
      limit?: number
      windowDays?: number
      pillar?: 'all' | 'compliance' | 'bpa' | 'identity'
    }
  ): Promise<PriorityAction[]> {
    const response = await apiClient.get<PriorityAction[]>(
      `/posture/priority-actions/tenant/${tenantId}`,
      { params: options }
    )
    return response.data ?? []
  },

  async getDriftAuditHistory(id: string): Promise<Array<{
    id: string
    driftId: string
    eventType: string
    triggeredBy: string
    triggeredByObjectId: string | null
    triggeredByDisplayName: string | null
    userRole: string | null
    previousStatus: string | null
    newStatus: string
    remediationSource: string | null
    comments: string | null
    eventTimestamp: string
    additionalMetadata: string | null
  }>> {
    const response = await apiClient.get<ApiResponse<Array<any>>>(`/configurationdrift/${id}/audit-history`)
    return response.data.data
  },


  // Tenant APIs
  async getTenants(): Promise<Tenant[]> {
    const response = await apiClient.get<ApiResponse<Tenant[]>>('/tenants')
    return response.data.data
  },

  async getTenant(id: string): Promise<Tenant> {
    const response = await apiClient.get<ApiResponse<Tenant>>(`/tenants/${id}`)
    return response.data.data
  },

  // Tenant scan settings APIs
  async getTenantScanSettings(tenantId: string): Promise<{
    tenantId: string
    scanFrequency: string
    scanScheduleEnabled: boolean
  }> {
    const response = await apiClient.get<{
      tenantId: string
      scanFrequency: string
      scanScheduleEnabled: boolean
    }>(`/tenants/${tenantId}/scan-settings`)
    return response.data
  },

  async updateTenantScanSettings(tenantId: string, settings: {
    scanFrequency: string
    scanScheduleEnabled: boolean
  }): Promise<{
    tenantId: string
    scanFrequency: string
    scanScheduleEnabled: boolean
  }> {
    const response = await apiClient.post<{
      tenantId: string
      scanFrequency: string
      scanScheduleEnabled: boolean
    }>(`/tenants/${tenantId}/scan-settings`, settings)
    return response.data
  },

  // User Insights settings APIs
  async getUserInsightsSettings(tenantId: string): Promise<{ tenantId: string; inactivityThresholdDays: number }> {
    const response = await apiClient.get<{ tenantId: string; inactivityThresholdDays: number }>(
      `/tenants/${tenantId}/user-insights-settings`
    )
    return response.data
  },

  async updateUserInsightsSettings(
    tenantId: string,
    settings: { inactivityThresholdDays: number }
  ): Promise<{ tenantId: string; inactivityThresholdDays: number }> {
    const response = await apiClient.post<{ tenantId: string; inactivityThresholdDays: number }>(
      `/tenants/${tenantId}/user-insights-settings`,
      settings
    )
    return response.data
  },

  async scanTenant(id: string): Promise<{jobId: string, tenantId: string, status: string, message: string, timestamp: string}> {
    // Returns immediately with job ID for async tracking
    const response = await apiClient.post(`/scan/tenant/${id}`, {}, {
      timeout: 30000 // 30 second timeout for job creation
    })
    return response.data
  },

  async getScanJobStatus(jobId: string): Promise<{
    id: string
    tenantId: string
    jobType: string
    status: string
    progressPercentage: number
    currentStep?: string
    statusMessage?: string
    createdAt: string
    startedAt?: string
    completedAt?: string
    errorMessage?: string
    resultData?: string
    createdBy: string
  }> {
    const response = await apiClient.get(`/scan/job/${jobId}`)
    return response.data
  },

  async getRecentScanJobs(tenantId: string, limit: number = 10): Promise<any[]> {
    const response = await apiClient.get(`/scan/tenant/${tenantId}/jobs?limit=${limit}`)
    return response.data
  },

  async getScanLimits(tenantId: string): Promise<any> {
    const response = await apiClient.get(`/scan/tenant/${tenantId}/limits`)
    return response.data
  },

  // Compliance APIs
  async getComplianceReport(tenantId: string, frameworkId?: string): Promise<any> {
    const params = frameworkId ? { frameworkId } : {}
    const response = await apiClient.get<ApiResponse<any>>(
      `/compliance/${tenantId}/report`,
      { params }
    )
    return response.data.data
  },

  async exportComplianceReport(tenantId: string, format: 'pdf' | 'excel' = 'pdf'): Promise<Blob> {
    const response = await apiClient.get(
      `/compliance/${tenantId}/export`,
      {
        params: { format },
        responseType: 'blob'
      }
    )
    return response.data
  },

  // Baseline Reports APIs
  async downloadBaselineReport(tenantId: string, format: 'csv' | 'html' = 'csv'): Promise<Blob> {
    const response = await apiClient.get(
      `/reports/baseline/${tenantId}/download`,
      {
        params: { format },
        responseType: 'blob'
      }
    )
    return response.data
  },

  // Drift Audit Export APIs
  async downloadDriftAuditReport(
    tenantId: string,
    format: 'csv' | 'html' = 'csv',
    startDate?: string,
    endDate?: string,
    eventTypes?: string[]
  ): Promise<Blob> {
    const params: Record<string, string> = { format }
    if (startDate) params.startDate = startDate
    if (endDate) params.endDate = endDate
    if (eventTypes?.length) params.eventTypes = eventTypes.join(',')

    const response = await apiClient.get(
      `/reports/driftaudit/${tenantId}/download`,
      {
        params,
        responseType: 'blob'
      }
    )
    return response.data
  },

  // Executive Report APIs
  async getExecutiveReportData(tenantId: string, periodDays: number = 30): Promise<ExecutiveReportData> {
    const response = await apiClient.get(
      `/executive-report/${tenantId}/data`,
      { params: { period: periodDays } }
    )
    const d = response.data
    return {
      tenantId: d.TenantId ?? d.tenantId ?? '',
      tenantName: d.TenantName ?? d.tenantName ?? '',
      generatedAt: d.GeneratedAt ?? d.generatedAt ?? '',
      reportPeriodDays: d.ReportPeriodDays ?? d.reportPeriodDays ?? 30,
      currentGuardianScore: d.CurrentGuardianScore ?? d.currentGuardianScore ?? null,
      periodStartGuardianScore: d.PeriodStartGuardianScore ?? d.periodStartGuardianScore ?? null,
      letterGrade: d.LetterGrade ?? d.letterGrade ?? 'N/A',
      rating: d.Rating ?? d.rating ?? 'No Data',
      secureScore: normalizeScoreWithTrend(d.SecureScore ?? d.secureScore),
      bpaScore: normalizeScoreWithTrend(d.BpaScore ?? d.bpaScore),
      mfaAdoption: normalizeScoreWithTrend(d.MfaAdoption ?? d.mfaAdoption),
      baselineCompliance: normalizeScoreWithTrend(d.BaselineCompliance ?? d.baselineCompliance),
      frameworkScores: (d.FrameworkScores ?? d.frameworkScores ?? []).map(normalizeFramework),
      metrics: normalizeMetrics(d.Metrics ?? d.metrics),
      guardianScoreTrend: (d.GuardianScoreTrend ?? d.guardianScoreTrend ?? []).map(normalizeDataPoint),
      executiveNarrative: d.ExecutiveNarrative ?? d.executiveNarrative ?? null,
    }
  },

  async downloadExecutiveReport(
    tenantId: string, format: 'pdf' | 'html' = 'pdf', periodDays: number = 30
  ): Promise<Blob> {
    const response = await apiClient.get(
      `/executive-report/${tenantId}/download`,
      {
        params: { format, period: periodDays },
        responseType: 'blob'
      }
    )
    return response.data
  },

  // DevOps Integration APIs
  async validateDevOpsConnection(): Promise<{ isValid: boolean; message: string }> {
    const response = await apiClient.get<ApiResponse<{ isValid: boolean; message: string }>>('/devopsintegration/validate-connection')
    return response.data.data
  },

  async triggerBaselineUpdate(tenantId: string, approvedChanges: ConfigurationDrift[]): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/devopsintegration/trigger-baseline-update',
      { tenantId, approvedChanges }
    )
    return response.data.data
  },

  async bulkApproveDrifts(driftIds: string[], comments?: string): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      '/configurationdrift/bulk-approve',
      { driftIds, comments }
    )
    return response.data.data
  },

  async processBatchedApprovals(tenantId?: string): Promise<{ 
    message: string
    processed: number
    pullRequests: Array<{
      tenantId: string
      driftCount: number
      prUrl: string
      prId: string
    }>
  }> {
    const url = tenantId 
      ? `/configurationdrift/process-batched-approvals?tenantId=${tenantId}`
      : '/configurationdrift/process-batched-approvals'
    
    const response = await apiClient.post(url)
    // Handle potential nested data structure
    return response.data?.data || response.data
  },

  // Tenant Management APIs
  async getTenantManagementList(): Promise<any[]> {
    const response = await apiClient.get('/tenantmanagement')
    return response.data
  },

  async onboardTenant(tenantData: any): Promise<any> {
    const response = await apiClient.post('/tenantmanagement/onboard', tenantData)
    return response.data
  },

  async updateTenant(tenantId: string, tenantData: any): Promise<any> {
    const response = await apiClient.put(`/tenantmanagement/${tenantId}`, tenantData)
    return response.data
  },

  async deactivateTenant(tenantId: string): Promise<void> {
    await apiClient.delete(`/tenantmanagement/${tenantId}`)
  },

  async testTenantAuth(tenantId: string): Promise<any> {
    const response = await apiClient.post(`/tenantmanagement/${tenantId}/test-auth`)
    return response.data
  },

  // User Management APIs
  async getCurrentUser(): Promise<any> {
    const response = await apiClient.get('/usermanagement/me')
    return response.data
  },

  async getUsers(): Promise<any[]> {
    const response = await apiClient.get('/usermanagement')
    return response.data
  },

  async getTenantUsers(tenantId: string): Promise<any[]> {
    const response = await apiClient.get(`/usermanagement/tenant/${tenantId}`)
    return response.data
  },

  async getUser(userObjectId: string): Promise<any> {
    const response = await apiClient.get(`/usermanagement/${userObjectId}`)
    return response.data
  },

  async assignUserToTenant(userObjectId: string, tenantId: string): Promise<any> {
    const response = await apiClient.post(`/usermanagement/${userObjectId}/tenants/${tenantId}`)
    return response.data
  },

  async removeUserFromTenant(userObjectId: string, tenantId: string): Promise<void> {
    await apiClient.delete(`/usermanagement/${userObjectId}/tenants/${tenantId}`)
  },

  async updateUserRole(userObjectId: string, role: string): Promise<any> {
    const response = await apiClient.put(`/usermanagement/${userObjectId}/role`, { role })
    return response.data
  },

  async deactivateUser(userObjectId: string): Promise<any> {
    const response = await apiClient.post(`/usermanagement/${userObjectId}/deactivate`)
    return response.data
  },

  async preRegisterUser(userData: {
    objectId: string
    email: string
    displayName?: string
    role: string
    tenantIds?: string[]
  }): Promise<any> {
    const response = await apiClient.post('/usermanagement/pre-register', userData)
    return response.data
  },

  async sendWelcomeEmail(userObjectId: string): Promise<{
    success: boolean
    message: string
    sentTo: string
    sentAt: string
  }> {
    const response = await apiClient.post(`/usermanagement/${userObjectId}/send-welcome-email`)
    return response.data
  },

  async getDevOpsConfiguration(): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/devopsintegration/configuration')
    return response.data.data
  },

  // Helper methods for dashboard data
  async getRecentActivity(tenantId: string): Promise<any[]> {
    if (import.meta.env.DEV) {
      console.log(`[${new Date().toISOString()}] Starting getRecentActivity for tenant: ${tenantId}`)
    }

    try {
      // Call the new backend activity endpoint which aggregates:
      // - Recent scan jobs
      // - Active drift detections
      // - Approval/rejection events with user accountability ("who did what")
      const response = await apiClient.get<any>(`/activity/tenant/${tenantId}?limit=15`)

      // Backend may return either a bare array or an envelope `{ data: [...] }`.
      const activities = Array.isArray(response.data) ? response.data : (response.data?.data || [])

      if (import.meta.env.DEV) {
        console.log(`getRecentActivity complete for tenant ${tenantId}: ${activities.length} activities`)
      }
      return activities
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(`getRecentActivity failed for tenant ${tenantId}, falling back to empty array:`, error)
      }

      // Fallback: return empty array if backend call fails
      // The UI will show "No recent activity" which is better than breaking
      return []
    }
  },

  async getDriftTrends(tenantId: string): Promise<any[]> {
    if (import.meta.env.DEV) {
      console.log(`[${new Date().toISOString()}] getDriftTrends called for tenant: ${tenantId}`)
    }

    try {
      // Get drift data for the last 30 days
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      if (import.meta.env.DEV) {
        console.log(`Fetching drifts from ${thirtyDaysAgo} to now`)
      }

      const driftResponse = await this.getConfigurationDrifts({
        tenantId,
        fromDate: thirtyDaysAgo,
        page: 1,
        limit: 1000 // Get all drifts in period
      })

      if (import.meta.env.DEV) {
        console.log(`API Response received:`, {
          itemCount: driftResponse.items?.length || 0,
          total: driftResponse.total,
          hasItems: !!driftResponse.items,
          firstItem: driftResponse.items?.[0] ? {
            detectedAt: driftResponse.items[0].detectedAt,
            severity: driftResponse.items[0].severity,
            component: driftResponse.items[0].component,
            status: driftResponse.items[0].status
          } : null,
          queryParams: {
            tenantId,
            fromDate: thirtyDaysAgo,
            page: 1,
            limit: 1000
          }
        })
      }

      // Continue even if no items - will show flat line at zero (compliant tenant)
      if (!driftResponse.items || driftResponse.items.length === 0) {
        if (import.meta.env.DEV) {
          console.warn(`No drift items found for tenant ${tenantId} in last 30 days`)
          console.warn(`Query params: tenantId=${tenantId}, fromDate=${thirtyDaysAgo}`)
          console.warn(`This could mean: 1) Tenant is 100% compliant, 2) Drifts are older than 30 days, or 3) Drifts have been cleaned up`)
          console.warn(`Continuing to show 30-day trend with zero counts`)
        }
        // Don't return early - continue to show zero-count trend line
      }

      // Group drifts by day and severity
      const dailyTrends = new Map<string, { critical: number, high: number, medium: number, low: number, info: number }>()

      // Initialize last 30 days with zero counts (using UTC to match database timestamps)
      for (let i = 0; i < 30; i++) {
        const date = new Date()
        date.setUTCHours(0, 0, 0, 0)  // Set to UTC midnight to ensure consistent date boundaries
        date.setUTCDate(date.getUTCDate() - i)  // Subtract days in UTC timezone
        const dateKey = date.toISOString().split('T')[0]
        dailyTrends.set(dateKey, { critical: 0, high: 0, medium: 0, low: 0, info: 0 })
      }

      if (import.meta.env.DEV) {
        console.log(`Initialized ${dailyTrends.size} days with zero counts`)
      }

      // Count drifts by day and severity
      let processedCount = 0
      let skippedCount = 0

      driftResponse.items.forEach(drift => {
        const driftDate = new Date(drift.detectedAt).toISOString().split('T')[0]
        const existing = dailyTrends.get(driftDate)

        if (existing) {
          const severity = drift.severity.toLowerCase()
          if (severity === 'critical') existing.critical++
          else if (severity === 'high') existing.high++
          else if (severity === 'medium') existing.medium++
          else if (severity === 'low') existing.low++
          else existing.info++
          processedCount++
        } else {
          skippedCount++
          if (import.meta.env.DEV && skippedCount <= 3) { // Log first 3 skipped items
            console.warn(`Drift skipped (date outside 30-day window): ${driftDate}`, drift)
          }
        }
      })

      if (import.meta.env.DEV) {
        console.log(`Processed ${processedCount} drifts, skipped ${skippedCount} (outside date range)`)
      }

      // Calculate total drifts across all severity levels
      let totalDrifts = 0
      dailyTrends.forEach(counts => {
        totalDrifts += counts.critical + counts.high + counts.medium + counts.low + counts.info
      })

      if (import.meta.env.DEV) {
        console.log(`Total drifts across all days: ${totalDrifts}`)
      }

      // Convert to array format for chart, aggregating every 4 days for readability
      // IMPORTANT: Aggregate counts instead of sampling to avoid losing drift data
      const trends: any[] = []
      const sortedDates = Array.from(dailyTrends.keys()).sort()

      // Aggregate every 4 days starting from oldest
      for (let i = 0; i < sortedDates.length; i += 4) {
        // Aggregate counts for this 4-day interval
        const intervalEnd = Math.min(i + 4, sortedDates.length)
        const aggregatedCounts = {
          critical: 0,
          high: 0,
          medium: 0,
          low: 0,
          info: 0
        }

        // Sum up counts for all days in this interval
        for (let j = i; j < intervalEnd; j++) {
          const counts = dailyTrends.get(sortedDates[j])
          if (!counts) continue
          aggregatedCounts.critical += counts.critical
          aggregatedCounts.high += counts.high
          aggregatedCounts.medium += counts.medium
          aggregatedCounts.low += counts.low
          aggregatedCounts.info += counts.info
        }

        // Use the first date of the interval as the label
        const intervalDate = sortedDates[i]
        trends.push({
          date: new Date(intervalDate).toISOString(),
          ...aggregatedCounts
        })
      }

      // Always include the most recent day separately if it's not already the last interval
      const mostRecentDate = sortedDates[sortedDates.length - 1]
      const mostRecentIsoDate = new Date(mostRecentDate).toISOString()
      const lastTrendDate = trends.length > 0 ? trends[trends.length - 1].date : null

      if (!lastTrendDate || lastTrendDate !== mostRecentIsoDate) {
        const counts = dailyTrends.get(mostRecentDate)
        if (counts) {
          trends.push({
            date: mostRecentIsoDate,
            ...counts
          })
        }
      }

      const finalTrends = trends.reverse() // Chronological order

      if (import.meta.env.DEV) {
        console.log(`Final trends array:`, {
          length: finalTrends.length,
          dateRange: finalTrends.length > 0 ? {
            start: finalTrends[0].date,
            end: finalTrends[finalTrends.length - 1].date
          } : null,
          samplePoint: finalTrends[0]
        })

        // Log each data point for debugging
        console.log(`COMPLETE DRIFT TRENDS DATA (${finalTrends.length} points):`)
        finalTrends.forEach((point, index) => {
          const total = point.critical + point.high + point.medium + point.low + point.info
          console.log(`  [${index}] ${point.date} -> Total: ${total} (Critical: ${point.critical}, High: ${point.high}, Medium: ${point.medium}, Low: ${point.low}, Info: ${point.info})`)
        })
      }

      return finalTrends

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error(`Error fetching drift trends for tenant ${tenantId}:`, error)
        if (error instanceof Error) {
          console.error('Error details:', {
            message: error.message,
            stack: error.stack
          })
        }
        // Return empty array on error - no fake data
        console.warn('Returning empty array due to error - chart will show "No trend data available"')
      }
      return []
    }
  },

  // Baseline Refresh API
  async refreshBaseline(tenantId: string, requestedBy: string): Promise<{
    success: boolean
    message: string
    jobId: string
    tenantId: string
  }> {
    const response = await apiClient.post(`/scan/baseline-refresh/${tenantId}`, {
      requestedBy,
      confirmationAcknowledged: true
    })
    return response.data
  },

  // Tenant Status APIs for concurrency control
  async getTenantStatus(tenantId: string): Promise<{
    tenantId: string
    hasActiveOperations: boolean
    queuedJobs: Array<{
      jobId: string
      jobType: string
      queuedAt: string
      priority: number
    }>
    runningJobs: Array<{
      jobId: string
      jobType: string
      status: string
      progressPercentage: number
      currentStep?: string
      startedAt?: string
    }>
    queuePosition?: number
    timestamp: string
  }> {
    const response = await apiClient.get(`/tenantstatus/${tenantId}/status`)
    return response.data
  },

  async getQueueStatus(): Promise<{
    queueLength: number
    runningJobsCount: number
    runningJobs: Array<any>
    timestamp: string
  }> {
    const response = await apiClient.get('/tenantstatus/queue/status')
    return response.data
  },

  async cancelQueuedJobs(tenantId: string): Promise<{
    tenantId: string
    message: string
    cancelledJobIds: string[]
    timestamp: string
  }> {
    const response = await apiClient.post(`/tenantstatus/${tenantId}/cancel-queued`)
    return response.data
  },

  // Admin Queue Management APIs
  async getDetailedQueueStatus(): Promise<any> {
    const response = await apiClient.get('/tenantstatus/admin/queue/detailed')
    return response.data
  },

  async emergencyStop(): Promise<{
    message: string
    cancelledJobIds: string[]
    note: string
    timestamp: string
  }> {
    const response = await apiClient.post('/tenantstatus/admin/emergency-stop')
    return response.data
  },

  async clearStuckJobs(): Promise<{
    message: string
    clearedJobIds: string[]
    timestamp: string
  }> {
    const response = await apiClient.post('/tenantstatus/admin/clear-stuck-jobs')
    return response.data
  },

  // Admin Scan Errors Dashboard API
  async getAdminScanErrors(hoursBack: number = 24): Promise<{
    summary: {
      totalErrors: number
      totalFailed: number
      totalPartial: number
      tenantsAffected: number
      hoursBack: number
    }
    tenantGroups: Array<{
      tenantId: string
      tenantName: string
      tenantDomain: string
      errorCount: number
      failedCount: number
      partialCount: number
      jobs: Array<{
        id: string
        jobType: string
        status: string
        statusMessage?: string
        progressPercentage: number
        currentStep?: string
        createdAt: string
        startedAt?: string
        completedAt?: string
        errorMessage?: string
        createdBy: string
      }>
    }>
    timestamp: string
  }> {
    const response = await apiClient.get(`/scan/admin/errors?hoursBack=${hoursBack}`)
    return response.data
  },

  // QA Policy Validation APIs
  async getQaValidation(): Promise<QaValidationSummary> {
    const response = await apiClient.get('/qa-validation')
    return response.data
  },

  async upsertQaValidation(policyEntityId: string, body: UpsertQaValidationRequest): Promise<QaPolicy> {
    const response = await apiClient.put(`/qa-validation/${policyEntityId}`, body)
    return response.data
  },

  // Tenant Resources APIs
  async getTenantMonitoredResources(tenantId: string): Promise<string[]> {
    const response = await apiClient.get<string[]>(`/tenants/${tenantId}/resources`)
    return response.data
  },

  async updateTenantMonitoredResources(tenantId: string, resources: string[]): Promise<void> {
    if (import.meta.env.DEV) {
      console.log('API updateTenantMonitoredResources called!');
      console.log('tenantId:', tenantId);
      console.log('resources:', resources);
      console.log('resources length:', resources.length);
    }

    try {
      await apiClient.put(`/tenants/${tenantId}/resources`, { resources })
      if (import.meta.env.DEV) {
        console.log('API PUT request completed successfully');
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('API PUT request failed:', error);
      }
      throw error;
    }
  },

  async getAvailableResources(): Promise<Record<string, string[]>> {
    const response = await apiClient.get<Record<string, string[]>>('/tenants/resources/available')
    return response.data
  },

  // User Settings APIs
  async getUserEmailPreferences(): Promise<{
    driftNotificationsEnabled: boolean
    driftNotificationEmail: string
    driftNotificationFormat: string
    emailPreferencesUpdatedAt: string
  }> {
    const response = await apiClient.get<{
      driftNotificationsEnabled: boolean
      driftNotificationEmail: string
      driftNotificationFormat: string
      emailPreferencesUpdatedAt: string
    }>('/usersettings/email-preferences')
    return response.data
  },

  async updateUserEmailPreferences(preferences: {
    driftNotificationsEnabled: boolean
    driftNotificationEmail?: string
    driftNotificationFormat: string
  }): Promise<{
    driftNotificationsEnabled: boolean
    driftNotificationEmail: string
    driftNotificationFormat: string
    emailPreferencesUpdatedAt: string
  }> {
    const response = await apiClient.put<{
      driftNotificationsEnabled: boolean
      driftNotificationEmail: string
      driftNotificationFormat: string
      emailPreferencesUpdatedAt: string
    }>('/usersettings/email-preferences', preferences)
    return response.data
  },

  async getTenantEmailPreferences(tenantId: string): Promise<{
    tenantId: string
    driftNotificationsEnabled: boolean
    driftNotificationEmail: string
    driftNotificationFormat: string
    updatedAt: string
    isGlobal: boolean
  }> {
    const response = await apiClient.get<{
      tenantId: string
      driftNotificationsEnabled: boolean
      driftNotificationEmail: string
      driftNotificationFormat: string
      updatedAt: string
      isGlobal: boolean
    }>(`/usersettings/email-preferences/tenant/${tenantId}`)
    return response.data
  },

  async updateTenantEmailPreferences(tenantId: string, preferences: {
    driftNotificationsEnabled: boolean
    driftNotificationEmail?: string
    driftNotificationFormat: string
  }): Promise<{
    tenantId: string
    driftNotificationsEnabled: boolean
    driftNotificationEmail: string
    driftNotificationFormat: string
    updatedAt: string
    isGlobal: boolean
  }> {
    const response = await apiClient.put<{
      tenantId: string
      driftNotificationsEnabled: boolean
      driftNotificationEmail: string
      driftNotificationFormat: string
      updatedAt: string
      isGlobal: boolean
    }>(`/usersettings/email-preferences/tenant/${tenantId}`, preferences)
    return response.data
  },

  async getAllTenantEmailPreferences(): Promise<Array<{
    tenantId: string
    driftNotificationsEnabled: boolean
    driftNotificationEmail: string
    driftNotificationFormat: string
    updatedAt: string
    isGlobal: boolean
  }>> {
    const response = await apiClient.get<Array<{
      tenantId: string
      driftNotificationsEnabled: boolean
      driftNotificationEmail: string
      driftNotificationFormat: string
      updatedAt: string
      isGlobal: boolean
    }>>('/usersettings/email-preferences/all-tenants')
    return response.data
  },

  // Tenant Notification Settings APIs (tenant-level settings)
  async getTenantNotificationSettings(tenantId: string): Promise<{
    tenantId: string
    rejectedDriftNotificationEmail?: string
    driftAlertNotificationEmail?: string
  }> {
    const response = await apiClient.get<{
      tenantId: string
      rejectedDriftNotificationEmail?: string
      driftAlertNotificationEmail?: string
    }>(`/tenants/${tenantId}/notification-settings`)
    return response.data
  },

  async updateTenantNotificationSettings(tenantId: string, settings: {
    rejectedDriftNotificationEmail?: string
    driftAlertNotificationEmail?: string
  }): Promise<{
    tenantId: string
    rejectedDriftNotificationEmail?: string
    driftAlertNotificationEmail?: string
  }> {
    const response = await apiClient.post<{
      tenantId: string
      rejectedDriftNotificationEmail?: string
      driftAlertNotificationEmail?: string
    }>(`/tenants/${tenantId}/notification-settings`, settings)
    return response.data
  },

  // SCuBA Compliance APIs
  async startScubaScan(tenantId: string): Promise<ScubaScanResponse> {
    const response = await apiClient.post<ScubaScanResponse>(`/scuba/scan/tenant/${tenantId}`)
    return response.data
  },

  async getScubaReport(tenantId: string): Promise<ScubaReport | null> {
    try {
      const response = await apiClient.get<ScubaReport>(`/scuba/report/tenant/${tenantId}`)
      return response.data
    } catch (error: any) {
      // Return null if no report exists (404)
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  async getScubaRisks(tenantId: string, limit: number = 10): Promise<ScubaSecurityRisk[]> {
    try {
      const response = await apiClient.get<ScubaSecurityRisk[]>(`/scuba/risks/tenant/${tenantId}`, {
        params: { limit }
      })
      return response.data
    } catch (error: any) {
      // Return empty array if no report exists
      if (error.response?.status === 404) {
        return []
      }
      throw error
    }
  },

  async getScubaSummary(tenantId: string): Promise<ScubaSummary> {
    try {
      const response = await apiClient.get<ScubaSummary>(`/scuba/summary/tenant/${tenantId}`)
      return response.data
    } catch (error: any) {
      // Return default empty summary if no report exists
      if (error.response?.status === 404) {
        return {
          tenantId,
          hasReport: false,
          complianceScore: 0,
          totalPasses: 0,
          totalFailures: 0,
          totalWarnings: 0,
          totalErrors: 0,
          totalManual: 0,
          totalControls: 0,
          products: []
        }
      }
      throw error
    }
  },

  // User Insights APIs
  async getUserInsights(tenantId: string): Promise<UserInsightsSummary | null> {
    try {
      const response = await apiClient.get<UserInsightsSummary>(`/userinsights/tenant/${tenantId}`)
      return response.data
    } catch (error: any) {
      // Return null if no insights data exists
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  async getUserInsightsLicenses(tenantId: string): Promise<LicenseSkuSummary[]> {
    try {
      const response = await apiClient.get<LicenseSkuSummary[]>(`/userinsights/tenant/${tenantId}/licenses`)
      return response.data
    } catch (error: any) {
      // Return empty array if no data exists
      if (error.response?.status === 404) {
        return []
      }
      throw error
    }
  },

  async getUserInsightsUsers(
    tenantId: string,
    filter: UserIssueFilter = 'All',
    page: number = 1,
    limit: number = 50,
    /**
     * When `true`, narrows the result server-side to Global Admins under
     * the active filter. The Container honors `?globalAdminsOnly=true` via
     * a `[FromQuery]` model-binding (case-insensitive). The Function App
     * proxies the query string transparently.
     *
     * Sent only when truthy so URLs stay clean for the default unscoped
     * call path — avoids a behavior change for older log readers and keeps
     * the cache key stable on existing callers.
     */
    globalAdminsOnly?: boolean,
  ): Promise<UserDetailListResult> {
    try {
      const params: Record<string, string | number | boolean> = { filter, page, limit }
      if (globalAdminsOnly) {
        params.globalAdminsOnly = true
      }
      const response = await apiClient.get<UserDetailListResult>(
        `/userinsights/tenant/${tenantId}/users`,
        { params }
      )
      return response.data
    } catch (error: any) {
      // Return empty result if no data exists
      if (error.response?.status === 404) {
        return {
          items: [],
          total: 0,
          page: 1,
          limit: 50,
          totalPages: 0
        }
      }
      throw error
    }
  },

  async refreshUserInsights(tenantId: string): Promise<RefreshResponse> {
    const response = await apiClient.post<RefreshResponse>(`/userinsights/tenant/${tenantId}/refresh`)
    return response.data
  },

  async getUserInsightsStatus(tenantId: string): Promise<RefreshStatusInfo> {
    const response = await apiClient.get<RefreshStatusInfo>(`/userinsights/tenant/${tenantId}/status`)
    return response.data
  },

  async getPhishResistantMfaSummary(tenantId: string): Promise<PhishResistantMfaSummary | null> {
    try {
      const response = await apiClient.get<PhishResistantMfaSummary>(
        `/userinsights/tenant/${tenantId}/phish-resistant-mfa`
      )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  async getUserInsightsTrends(tenantId: string, days: number = 90): Promise<UserInsightsTrendsResult | null> {
    try {
      const response = await apiClient.get<UserInsightsTrendsResult>(
        `/userinsights/tenant/${tenantId}/trends`,
        { params: { days } }
      )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  // Microsoft Secure Score APIs

  async getSecureScoreSummary(tenantId: string): Promise<SecureScoreSummary | null> {
    try {
      const response = await apiClient.get<SecureScoreSummary>(`/securescore/tenant/${tenantId}/summary`)
      return response.data
    } catch (error: any) {
      // Return null if no data available or not configured
      if (error.response?.status === 404 || error.response?.status === 500) {
        console.warn('Secure Score data not available for tenant:', tenantId)
        return null
      }
      throw error
    }
  },

  async getSecureScore(tenantId: string): Promise<SecureScoreResult | null> {
    try {
      const response = await apiClient.get<SecureScoreResult>(`/securescore/tenant/${tenantId}`)
      return response.data
    } catch (error: any) {
      // Return null if no data available
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  async getSecureScoreRecommendations(tenantId: string): Promise<SecureScoreControlProfile[]> {
    try {
      const response = await apiClient.get<SecureScoreControlProfile[]>(`/securescore/tenant/${tenantId}/recommendations`)
      return response.data
    } catch (error: any) {
      // Return empty array if no recommendations available
      if (error.response?.status === 404) {
        return []
      }
      throw error
    }
  },

  async refreshSecureScore(tenantId: string): Promise<SecureScoreSummary | null> {
    try {
      const response = await apiClient.post<SecureScoreSummary>(`/securescore/tenant/${tenantId}/refresh`)
      return response.data
    } catch (error: any) {
      // Return null if refresh fails
      if (error.response?.status === 404 || error.response?.status === 500) {
        console.warn('Failed to refresh Secure Score for tenant:', tenantId)
        return null
      }
      throw error
    }
  },

  // Config-as-Code Portal APIs (DevOps Pull Requests)
  async getDevOpsPullRequests(params: {
    tenantId: string
    page?: number
    pageSize?: number
    status?: string
  }): Promise<{
    items: DevOpsPullRequest[]
    totalCount: number
    page: number
    pageSize: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }> {
    const response = await apiClient.get('/devopspullrequests', { params })
    return response.data?.data || response.data
  },

  async getDevOpsPullRequest(id: string, tenantId: string): Promise<DevOpsPullRequest | null> {
    try {
      const response = await apiClient.get(`/devopspullrequests/${id}`, {
        params: { tenantId }
      })
      return response.data?.data || response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  async getDevOpsPullRequestDrifts(id: string, tenantId: string): Promise<DevOpsPullRequestDrift[]> {
    try {
      const response = await apiClient.get(`/devopspullrequests/${id}/drifts`, {
        params: { tenantId }
      })
      return response.data?.data || response.data || []
    } catch (error: any) {
      if (error.response?.status === 404) {
        return []
      }
      throw error
    }
  },

  async syncDevOpsPullRequestStatus(id: string, tenantId: string): Promise<void> {
    await apiClient.post(`/devopspullrequests/${id}/sync`, null, {
      params: { tenantId }
    })
  },

  async syncAllDevOpsPullRequests(tenantId: string): Promise<void> {
    await apiClient.post('/devopspullrequests/sync-all', null, {
      params: { tenantId }
    })
  },

  // Admin Consent APIs (Multi-Tenant Authentication)
  async initiateAdminConsent(tenantId: string): Promise<AdminConsentInitiateResponse> {
    const response = await apiClient.post<AdminConsentInitiateResponse>(`/tenant-consent/${tenantId}/initiate`)
    return response.data
  },

  async processAdminConsentCallback(request: AdminConsentCallbackRequest): Promise<AdminConsentCallbackResponse> {
    const response = await apiClient.post<AdminConsentCallbackResponse>('/tenant-consent/callback', request)
    return response.data
  },

  async getAdminConsentStatus(tenantId: string): Promise<AdminConsentStatus> {
    const response = await apiClient.get<AdminConsentStatus>(`/tenant-consent/${tenantId}/status`)
    return response.data
  },

  async verifyAndRecordConsent(tenantId: string): Promise<AdminConsentCallbackResponse> {
    const response = await apiClient.post<AdminConsentCallbackResponse>(`/tenant-consent/${tenantId}/verify-and-record`)
    return response.data
  },

  async revokeAdminConsent(tenantId: string): Promise<AdminConsentRevokeResponse> {
    const response = await apiClient.post<AdminConsentRevokeResponse>(`/tenant-consent/${tenantId}/revoke`)
    return response.data
  },

  // Onboarding Email API
  async sendOnboardingEmail(
    tenantId: string,
    request: SendOnboardingEmailRequest
  ): Promise<SendOnboardingEmailResponse> {
    const response = await apiClient.post<SendOnboardingEmailResponse>(
      `/tenant-consent/${tenantId}/send-onboarding-email`,
      request
    )
    return response.data
  },

  // ==========================================================================
  // Standards Compliance APIs (OPA-based multi-framework evaluation)
  // ==========================================================================

  // --- Standards Management ---

  /**
   * Get all available compliance standards/frameworks
   */
  async getComplianceStandards(): Promise<ComplianceStandard[]> {
    try {
      const response = await apiClient.get<ApiResponse<ComplianceStandard[]>>('/compliance/standards')
      return response.data.data || response.data || []
    } catch (error: unknown) {
      console.error('Failed to fetch compliance standards:', error)
      return []
    }
  },

  /**
   * Get a specific compliance standard by ID
   */
  async getComplianceStandard(standardId: string): Promise<ComplianceStandard | null> {
    try {
      const response = await apiClient.get<ApiResponse<ComplianceStandard>>(`/compliance/standards/${standardId}`)
      return response.data.data || response.data || null
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Get all policies within a specific standard
   */
  async getStandardPolicies(standardId: string): Promise<CompliancePolicy[]> {
    try {
      const response = await apiClient.get<ApiResponse<CompliancePolicy[]>>(`/compliance/standards/${standardId}/policies`)
      return response.data.data || response.data || []
    } catch (error: unknown) {
      console.error('Failed to fetch standard policies:', error)
      return []
    }
  },

  // --- Tenant Standards Configuration ---

  /**
   * Get standards assigned to a tenant
   * Transforms backend TenantStandardDto to frontend TenantStandardAssignment
   */
  async getTenantStandards(tenantId: string): Promise<TenantStandardAssignment[]> {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(`/compliance/tenant/${tenantId}/standards`)
      const data = response.data.data || response.data || []

      // Transform backend DTO (Code, Name) to frontend type (standardCode, standardName)
      return data.map((s: any) => ({
        id: s.Id ?? s.id ?? '',
        tenantId: tenantId,
        standardId: s.Id ?? s.id ?? '',
        standardCode: s.Code ?? s.code ?? '',
        standardName: s.Name ?? s.name ?? '',
        isEnabled: s.IsEnabled ?? s.isEnabled ?? false,
        weight: s.Weight ?? s.weight ?? 50,
        includeInGuardianScore: s.IncludeInGuardianScore ?? s.includeInGuardianScore ?? false,
        assignedAt: s.AssignedAt ?? s.assignedAt ?? '',
        assignedBy: s.AssignedBy ?? s.assignedBy ?? ''
      }))
    } catch (error: unknown) {
      console.error('Failed to fetch tenant standards:', error)
      return []
    }
  },

  /**
   * Assign a standard to a tenant
   */
  async assignTenantStandard(tenantId: string, data: AssignStandardRequest): Promise<TenantStandardAssignment> {
    const response = await apiClient.post<ApiResponse<TenantStandardAssignment>>(
      `/compliance/tenant/${tenantId}/standards`,
      data
    )
    return response.data.data || response.data
  },

  /**
   * Update a tenant's standard configuration (weight, enabled, etc.)
   */
  async updateTenantStandard(
    tenantId: string,
    standardId: string,
    data: UpdateTenantStandardRequest
  ): Promise<TenantStandardAssignment> {
    const response = await apiClient.put<ApiResponse<TenantStandardAssignment>>(
      `/compliance/tenant/${tenantId}/standards/${standardId}`,
      data
    )
    return response.data.data || response.data
  },

  /**
   * Bulk update tenant standard configurations
   */
  async bulkUpdateTenantStandards(
    tenantId: string,
    updates: Array<{ standardId: string } & UpdateTenantStandardRequest>
  ): Promise<TenantStandardAssignment[]> {
    const response = await apiClient.put<ApiResponse<TenantStandardAssignment[]>>(
      `/compliance/tenant/${tenantId}/standards/bulk`,
      { updates }
    )
    return response.data.data || response.data || []
  },

  /**
   * Toggle whether a standard is included in the Guardian composite score.
   * Uses the existing update endpoint, preserving current isEnabled and weight values.
   */
  async toggleStandardIncludeInScore(
    tenantId: string,
    standardId: string,
    data: UpdateTenantStandardRequest
  ): Promise<TenantStandardAssignment> {
    const response = await apiClient.put<ApiResponse<TenantStandardAssignment>>(
      `/compliance/tenant/${tenantId}/standards/${standardId}`,
      data
    )
    return response.data.data || response.data
  },

  // --- Evaluations ---

  /**
   * Trigger a compliance evaluation for a tenant
   */
  async triggerComplianceEvaluation(tenantId: string, options?: { standardIds?: string[]; forceRefresh?: boolean }): Promise<EvaluationResult> {
    const response = await apiClient.post<ApiResponse<EvaluationResult>>(
      `/compliance/tenant/${tenantId}/evaluate`,
      options || {},
      { timeout: 300000 } // 5 minute timeout - evaluation includes Graph API + PowerShell data collection
    )
    return response.data.data || response.data
  },

  /**
   * Get all evaluations for a tenant
   */
  async getComplianceEvaluations(tenantId: string): Promise<ComplianceEvaluation[]> {
    try {
      const response = await apiClient.get<ApiResponse<ComplianceEvaluation[]>>(`/compliance/tenant/${tenantId}/evaluations`)
      return response.data.data || response.data || []
    } catch (error: unknown) {
      console.error('Failed to fetch evaluations:', error)
      return []
    }
  },

  /**
   * Get a specific evaluation by ID
   */
  async getComplianceEvaluation(tenantId: string, evaluationId: string): Promise<ComplianceEvaluation | null> {
    try {
      const response = await apiClient.get<ApiResponse<ComplianceEvaluation>>(
        `/compliance/tenant/${tenantId}/evaluations/${evaluationId}`
      )
      return response.data.data || response.data || null
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  // --- Scores ---

  /**
   * Get current compliance scores for a tenant (summary with framework scores)
   * Maps backend ComplianceScoreDto to frontend ComplianceSummary
   */
  async getComplianceScores(tenantId: string): Promise<ComplianceSummary | null> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(`/compliance/tenant/${tenantId}/scores`)
      const data = response.data.data || response.data

      if (!data) return null

      // Transform backend StandardScores to include display-friendly aliases
      const standardScores = (data.StandardScores ?? data.standardScores ?? []).map((s: any) => ({
        standardId: s.StandardId ?? s.standardId,
        standardCode: s.StandardCode ?? s.standardCode ?? '',
        standardName: s.StandardName ?? s.standardName ?? '',
        rawScore: s.RawScore ?? s.rawScore ?? 0,
        adjustedScore: s.AdjustedScore ?? s.adjustedScore ?? 0,
        weightedScore: s.WeightedScore ?? s.weightedScore ?? 0,
        weight: s.Weight ?? s.weight ?? 0,
        passedControls: s.PassedControls ?? s.passedControls ?? 0,
        failedControls: s.FailedControls ?? s.failedControls ?? 0,
        waivedControls: s.WaivedControls ?? s.waivedControls ?? 0,
        totalControls: s.TotalControls ?? s.totalControls ?? 0,
        lastEvaluatedAt: s.LastEvaluatedAt ?? s.lastEvaluatedAt,
        isEnabled: s.IsEnabled ?? s.isEnabled ?? true,
        includeInGuardianScore: s.IncludeInGuardianScore ?? s.includeInGuardianScore ?? true
      }))

      // Create display-friendly framework scores alias for UI components
      const frameworkScores = standardScores.map((s: any) => ({
        standardId: s.standardId,
        code: s.standardCode,
        name: s.standardName,
        score: s.weightedScore,  // Canonical BPA score (criticality-weighted) — see commit b6c1e07
        passedControls: s.passedControls,
        failedControls: s.failedControls,
        totalControls: s.totalControls,
        waivedControls: s.waivedControls,
        weight: s.weight,
        includeInGuardianScore: s.includeInGuardianScore,
        lastEvaluatedAt: s.lastEvaluatedAt
      }))

      // Extract collection health if present (Track C)
      const rawHealth = data.CollectionHealth ?? data.collectionHealth
      const collectionHealth = rawHealth
        ? rawHealth.map((h: any) => ({
            workload: h.Workload ?? h.workload,
            health: {
              graphCollected: h.Health?.GraphCollected ?? h.health?.graphCollected ?? false,
              powerShellCollected: h.Health?.PowerShellCollected ?? h.health?.powerShellCollected ?? false,
              graphCollectedAt: h.Health?.GraphCollectedAt ?? h.health?.graphCollectedAt ?? null,
              powerShellCollectedAt: h.Health?.PowerShellCollectedAt ?? h.health?.powerShellCollectedAt ?? null,
              errors: h.Health?.Errors ?? h.health?.errors ?? [],
            },
          }))
        : undefined

      return {
        tenantId: data.TenantId ?? data.tenantId ?? tenantId,
        overallScore: data.OverallScore ?? data.overallScore ?? 0,
        totalPassedControls: data.TotalPassedControls ?? data.totalPassedControls ?? 0,
        totalFailedControls: data.TotalFailedControls ?? data.totalFailedControls ?? 0,
        totalWaivedControls: data.TotalWaivedControls ?? data.totalWaivedControls ?? 0,
        totalControls: data.TotalControls ?? data.totalControls ?? 0,
        lastEvaluatedAt: data.LastEvaluatedAt ?? data.lastEvaluatedAt,
        standardScores,
        frameworkScores,  // Alias for backward compatibility
        // Compute violation counts from failed controls for UI display
        totalViolations: data.TotalFailedControls ?? data.totalFailedControls ?? 0,
        collectionHealth,
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Get score history for trend charts
   * Transforms backend ScoreHistoryResponseDto to frontend ComplianceTrendPoint[]
   */
  async getComplianceScoreHistory(tenantId: string, days: number = 90): Promise<ComplianceTrendPoint[]> {
    try {
      // Backend returns ScoreHistoryResponseDto with overallHistory and standardHistory
      interface ScoreHistoryItem {
        snapshotDate: string
        score: number
        passedControls?: number
        totalControls?: number
        standardId?: string
        standardCode?: string
      }
      interface ScoreHistoryResponse {
        tenantId: string
        overallHistory: ScoreHistoryItem[]
        standardHistory: Record<string, ScoreHistoryItem[]>
      }

      const response = await apiClient.get<ApiResponse<ScoreHistoryResponse>>(
        `/compliance/tenant/${tenantId}/scores/history`,
        { params: { days } }
      )

      const data = response.data.data || response.data
      if (!data || !data.overallHistory) {
        return []
      }

      // Transform to ComplianceTrendPoint[] format
      // Group by date, combining overall score with framework scores
      const dateMap = new Map<string, ComplianceTrendPoint>()

      // Add overall scores first
      for (const item of data.overallHistory) {
        const dateKey = item.snapshotDate.split('T')[0] // Normalize to date only
        dateMap.set(dateKey, {
          date: item.snapshotDate,
          overallScore: Number(item.score),
          frameworkScores: []
        })
      }

      // Add framework scores
      if (data.standardHistory) {
        for (const [code, history] of Object.entries(data.standardHistory)) {
          for (const item of history) {
            const dateKey = item.snapshotDate.split('T')[0]
            const point = dateMap.get(dateKey)
            if (point) {
              point.frameworkScores.push({
                code: code,
                score: Number(item.score)
              })
            }
          }
        }
      }

      // Convert to array and sort by date
      return Array.from(dateMap.values()).sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )
    } catch (error: unknown) {
      console.error('Failed to fetch score history:', error)
      return []
    }
  },

  /**
   * Get raw per-evaluation score events for a tenant (no day-bucket collapse).
   * Backend returns newest-first with deltas computed server-side against the
   * immediately prior event; the oldest event in window has delta = null.
   */
  async getComplianceScoreEvents(tenantId: string, limit: number = 20): Promise<ScoreEvent[]> {
    try {
      const response = await apiClient.get<ApiResponse<ScoreEvent[]>>(
        `/compliance/tenant/${tenantId}/scores/events`,
        { params: { limit } }
      )
      const data = response.data.data ?? response.data
      return Array.isArray(data) ? data : []
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return []
      }
      throw error
    }
  },

  /**
   * Get per-control change feed for a tenant — one row per policy flip
   * (Pass↔Fail, waiver applied, waiver lapsed) since the prior evaluation.
   * Backend returns newest-first with per-row signed `scoreDelta` computed
   * server-side; consumers must not re-sort or recompute.
   */
  async getControlChanges(tenantId: string, limit: number = 20): Promise<ControlChange[]> {
    try {
      const response = await apiClient.get<ApiResponse<ControlChange[]>>(
        `/compliance/tenant/${tenantId}/control-changes`,
        { params: { limit } }
      )
      const data = response.data.data ?? response.data
      return Array.isArray(data) ? data : []
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return []
      }
      throw error
    }
  },

  // --- Violation Summary ---

  /**
   * Get violation summary counts for a tenant (from dedicated summary endpoint).
   * Returns status counts, criticality/framework/workload breakdowns, and collection health.
   *
   * @param options.onlyStarred When false, the server includes findings from
   *   unstarred frameworks (matches the violations list endpoint's `onlyStarred=false`
   *   behavior). When true or undefined, the param is omitted and the server defaults
   *   to starred-only — keeps cache keys stable for the common case.
   */
  async getViolationSummary(
    tenantId: string,
    options?: { onlyStarred?: boolean }
  ): Promise<ViolationSummaryResponse | null> {
    try {
      const url = options?.onlyStarred === false
        ? `/compliance/tenant/${tenantId}/violations/summary?onlyStarred=false`
        : `/compliance/tenant/${tenantId}/violations/summary`
      const response = await apiClient.get<ApiResponse<any>>(url)
      const data = response.data.data || response.data
      if (!data) return null

      return {
        tenantId: data.TenantId ?? data.tenantId ?? tenantId,
        open: data.Open ?? data.open ?? 0,
        waived: data.Waived ?? data.waived ?? 0,
        resolved: data.Resolved ?? data.resolved ?? 0,
        pass: data.Pass ?? data.pass ?? 0,
        byCriticality: data.ByCriticality ?? data.byCriticality ?? {},
        byFramework: data.ByFramework ?? data.byFramework ?? {},
        byWorkload: data.ByWorkload ?? data.byWorkload ?? {},
        totalControls: data.TotalControls ?? data.totalControls ?? 0,
        collectionHealth: (data.CollectionHealth ?? data.collectionHealth ?? []).map((h: any) => ({
          workload: h.Workload ?? h.workload,
          health: {
            graphCollected: h.Health?.GraphCollected ?? h.health?.graphCollected ?? false,
            powerShellCollected: h.Health?.PowerShellCollected ?? h.health?.powerShellCollected ?? false,
            graphCollectedAt: h.Health?.GraphCollectedAt ?? h.health?.graphCollectedAt ?? null,
            powerShellCollectedAt: h.Health?.PowerShellCollectedAt ?? h.health?.powerShellCollectedAt ?? null,
            errors: h.Health?.Errors ?? h.health?.errors ?? [],
          },
        })),
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  // --- Violations ---

  /**
   * Get violations for a tenant with optional filtering
   * Transforms frontend filter params to match backend ViolationFilterDto
   */
  async getComplianceViolations(tenantId: string, filters?: ViolationFilters): Promise<PaginatedResponse<ComplianceViolation>> {
    try {
      // Transform filter params to match backend ViolationFilterDto
      // Backend uses PageNumber/PageSize, frontend may pass pageNumber/pageSize
      // onlyStarred is omitted when undefined so the server default
      // (starred-only) applies; pass false explicitly to include unstarred
      // frameworks (e.g., the Violations page "Include unstarred frameworks" toggle).
      const backendParams = filters ? {
        status: filters.status,
        criticality: filters.criticality,
        category: filters.category,
        product: filters.product,
        standardId: filters.standardId,
        ...(filters.onlyStarred !== undefined ? { onlyStarred: filters.onlyStarred } : {}),
        pageNumber: filters.pageNumber ?? 1,
        pageSize: filters.pageSize ?? 50
      } : undefined

      const response = await apiClient.get<ApiResponse<PaginatedResponse<ComplianceViolation>>>(
        `/compliance/tenant/${tenantId}/violations`,
        { params: backendParams }
      )
      const data = response.data.data || response.data

      return {
        items: data?.items ?? [],
        total: data?.total ?? 0,
        pageNumber: data?.pageNumber ?? 1,
        pageSize: data?.pageSize ?? 50,
        totalPages: data?.totalPages ?? 0
      }
    } catch (error: unknown) {
      console.error('Failed to fetch violations:', error)
      return { items: [], total: 0, pageNumber: 1, pageSize: 50, totalPages: 0 }
    }
  },

  /**
   * Get a specific violation by ID
   */
  async getComplianceViolation(tenantId: string, violationId: string): Promise<ComplianceViolation | null> {
    try {
      const response = await apiClient.get<ApiResponse<ComplianceViolation>>(
        `/compliance/tenant/${tenantId}/violations/${violationId}`
      )
      return response.data.data || response.data || null
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  // --- Waivers ---

  /**
   * Get all waivers for a tenant
   */
  async getComplianceWaivers(tenantId: string): Promise<TenantPolicyWaiver[]> {
    try {
      const response = await apiClient.get<ApiResponse<TenantPolicyWaiver[]>>(`/compliance/tenant/${tenantId}/waivers`)
      return response.data.data || response.data || []
    } catch (error: unknown) {
      console.error('Failed to fetch waivers:', error)
      return []
    }
  },

  /**
   * Create a waiver for a policy violation
   */
  async createComplianceWaiver(tenantId: string, data: CreateWaiverRequest): Promise<TenantPolicyWaiver> {
    const response = await apiClient.post<ApiResponse<TenantPolicyWaiver>>(
      `/compliance/tenant/${tenantId}/waivers`,
      data
    )
    return response.data.data || response.data
  },

  /**
   * Delete (deactivate) a waiver
   */
  async deleteComplianceWaiver(tenantId: string, waiverId: string): Promise<void> {
    await apiClient.delete(`/compliance/tenant/${tenantId}/waivers/${waiverId}`)
  },

  /**
   * Update an existing waiver
   */
  async updateComplianceWaiver(tenantId: string, waiverId: string, data: UpdateWaiverRequest): Promise<TenantPolicyWaiver> {
    const response = await apiClient.put<ApiResponse<TenantPolicyWaiver>>(
      `/compliance/tenant/${tenantId}/waivers/${waiverId}`,
      data
    )
    return response.data.data || response.data
  },

  // --- OAuth App Permissions ---

  /**
   * Get summary of OAuth app risk distribution for a tenant
   */
  async getOAuthAppSummary(tenantId: string): Promise<OAuthAppSummary> {
    const response = await apiClient.get(`/oauth-apps/tenant/${tenantId}/summary`)
    return response.data?.data ?? response.data
  },

  /**
   * Get paginated list of OAuth apps with optional filtering
   */
  async getOAuthApps(
    tenantId: string,
    page = 1,
    pageSize = 20,
    riskTier?: string,
    appType?: string,
    status?: string,
    verified?: string,
    search?: string
  ): Promise<OAuthAppsPagedResponse> {
    const response = await apiClient.get(`/oauth-apps/tenant/${tenantId}`, {
      params: { page, pageSize, riskTier, appType, status, verified, search }
    })
    return response.data?.data ?? response.data
  },

  /**
   * Get detailed information for a specific OAuth app including permissions
   */
  async getOAuthAppDetail(tenantId: string, id: string): Promise<OAuthAppDetail> {
    const response = await apiClient.get(`/oauth-apps/tenant/${tenantId}/${id}`)
    return response.data?.data ?? response.data
  },

  /**
   * Acknowledge or flag an OAuth app with a reason and optional expiry
   */
  async acknowledgeOAuthApp(tenantId: string, appId: string, request: AcknowledgeOAuthAppRequest): Promise<OAuthAppDetail> {
    const response = await apiClient.put(`/oauth-apps/tenant/${tenantId}/${appId}/acknowledge`, request)
    return response.data?.data ?? response.data
  },

  /**
   * Trigger a sync of OAuth apps from Microsoft Graph for a tenant
   */
  async syncOAuthApps(tenantId: string): Promise<void> {
    await apiClient.post(`/oauth-apps/tenant/${tenantId}/sync`)
  },

  /**
   * Recent OAuth-consent feed for the App Posture page (§8.5).
   * Returns up to `limit` rows ordered by GrantedAt DESC.
   */
  async getOAuthRecentConsents(
    tenantId: string,
    limit = 10,
  ): Promise<ConsentRecordRow[]> {
    const response = await apiClient.get(`/oauth-apps/recent-consents`, {
      params: { tenantId, limit },
    })
    const payload = response.data?.data ?? response.data
    return Array.isArray(payload) ? (payload as ConsentRecordRow[]) : []
  },

  /**
   * Operation x Scope permission heatmap for the App Posture page (§8.3).
   * Server-side aggregation over the tenant's classified OAuth permissions.
   */
  async getOAuthPermissionHeatmap(
    tenantId: string,
  ): Promise<PermissionHeatmap> {
    const response = await apiClient.get(`/oauth-apps/permission-heatmap`, {
      params: { tenantId },
    })
    return response.data?.data ?? response.data
  },

  // --- OPA Health ---

  /**
   * Get OPA service health status. Backend returns camelCase via OpaHealthDto.
   */
  async getOpaHealth(): Promise<OpaHealthStatus> {
    try {
      const response = await apiClient.get<ApiResponse<OpaHealthStatus>>('/opa/health')
      const data = response.data.data || response.data

      return {
        isHealthy: data?.isHealthy ?? false,
        opaVersion: data?.opaVersion ?? '',
        checkedAt: data?.checkedAt ?? new Date().toISOString(),
        loadedBundles: data?.loadedBundles ?? [],
        errorMessage: data?.errorMessage
      }
    } catch (error: unknown) {
      console.error('Failed to fetch OPA health:', error)
      return {
        isHealthy: false,
        opaVersion: '',
        loadedBundles: [],
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        checkedAt: new Date().toISOString()
      }
    }
  },
}

// DevOps Pull Request types
export interface DevOpsPullRequest {
  id: string
  tenantId: string
  pullRequestId: string
  pullRequestUrl: string
  title: string
  description?: string
  branchName?: string
  status: 'Active' | 'Merged' | 'Abandoned'
  triggeredByEmail?: string
  triggeredByDisplayName?: string
  driftCount: number
  createdAt: string
  mergedAt?: string
  mergedBy?: string
  drifts?: DevOpsPullRequestDrift[]
}

export interface DevOpsPullRequestDrift {
  driftId: string
  component: string
  setting: string
  approvedBy?: string
  approvedAt?: string
  severity?: string
}

// Admin Consent types (Multi-Tenant Authentication)
export interface AdminConsentInitiateResponse {
  consentUrl: string
  tenantId: string
  tenantName: string
  expiresInMinutes: number
}

export interface AdminConsentCallbackRequest {
  tenantId: string | null
  state: string
  adminConsent?: string
  error?: string
  errorDescription?: string
}

export interface AdminConsentCallbackResponse {
  success: boolean
  tenantId: string
  tenantName?: string
  authenticationMode?: string
  permissionsGranted?: number
  message?: string
  errorMessage?: string
}

export interface AdminConsentStatus {
  tenantId: string
  tenantName: string
  authenticationMode: 'PerTenant' | 'MultiTenant'
  isMultiTenantEnabled: boolean
  consentStatus: {
    isValid: boolean
    grantedAt?: string
    grantedBy?: string
    errorMessage?: string
  }
  adminConsentRevoked: boolean
  adminConsentRevokedAt?: string
  servicePrincipalObjectId?: string
}

export interface AdminConsentRevokeResponse {
  success: boolean
  tenantId: string
  tenantName: string
  authenticationMode: string
  message: string
  timestamp: string
}

// Onboarding Email types
export interface SendOnboardingEmailRequest {
  recipientEmail: string
  recipientName: string
}

export interface SendOnboardingEmailResponse {
  success: boolean
  message: string
  sentTo: string
  sentAt: string
}

// Self-Service Sign-Up types
export interface InitiateSignUpRequest {
  customerEmail: string
  customerName: string
  companyName: string
}

export interface InitiateSignUpResponse {
  success: boolean
  sessionId?: string
  consentUrl?: string
  error?: string
}

export interface CompleteSignUpRequest {
  tenantId: string
  state: string
  adminConsent: boolean
  error?: string
  errorDescription?: string
}

export interface CompleteSignUpResponse {
  success: boolean
  tenantId?: string
  tenantName?: string
  error?: string
}

export interface SignUpStatusResponse {
  sessionId: string
  status: string
  tenantId?: string
  tenantName?: string
  createdAt: string
  completedAt?: string
}

// Self-Service Sign-Up API functions (public, no auth required)
export async function initiateSignUp(data: InitiateSignUpRequest): Promise<InitiateSignUpResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/signup/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        success: false,
        error: errorData.error || `Request failed with status ${response.status}`,
      }
    }

    return response.json()
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Network error occurred',
    }
  }
}

export async function completeSignUp(data: CompleteSignUpRequest): Promise<CompleteSignUpResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/signup/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        success: false,
        error: errorData.error || `Request failed with status ${response.status}`,
      }
    }

    return response.json()
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Network error occurred',
    }
  }
}

export async function getSignUpStatus(sessionId: string): Promise<SignUpStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/signup/status/${sessionId}`)

  if (!response.ok) {
    throw new Error(`Failed to get status: ${response.status}`)
  }

  return response.json()
}

export default apiClient
